import debug from 'debug';
import { uniqueId } from 'lodash-es';

const log = debug('itp:core:poller');

export interface PollerOptions {
  name?: string;
  callback(poller: Poller): any;
  interval?: number;
  errback?(error: any): void;
}

export class Poller {
  readonly options: PollerOptions;

  readonly uid: string;
  readonly name: string;
  readonly logPrefix: string;
  protected counter: number = 0;

  private _lock: boolean | null = null;
  private _tid: number | null = null;
  private _stop: boolean = false;
  private _cb: Function | null = null;

  readonly interval: number = 3000;
  readonly callback: Function;

  public isRunning: boolean = false;

  constructor(options: PollerOptions) {
    this.options = options;

    this.uid = uniqueId();
    this.name = options.name ? `${this.uid}|${options.name}` : this.uid;
    if (options.interval != null) {
      this.interval = options.interval;
    }
    this.callback = options.callback;
    this.logPrefix = `Poller[${this.name}] `;

    log('%s Poller configured. timeout: %sms', this.logPrefix, this.interval);
  }

  start() {
    if (this._tid != null) {
      return;
    }

    log('%s Starting...', this.logPrefix);
    this._stop = false;
    this.schedule();
    return this;
  }

  schedule() {
    if (this._tid != null) {
      log(
        '%s Scheduling run #%s failed, Timeout ID already set to %s.',
        this.logPrefix,
        this.counter,
        this._tid,
      );
      return;
    }

    if (this._stop) {
      log(
        '%s Scheduling run #%s failed, STOP flag is ON.',
        this.logPrefix,
        this.counter,
      );
      this.isRunning = false;
      return;
    }

    if (!this._cb) {
      this._cb = () => this._exec();
    }

    if (this._tid == null) {
      this._tid = window.setInterval(this._cb, this.interval);
      log(
        '%s #%s Scheduling callback. (id=%s)',
        this.logPrefix,
        this.counter,
        this._tid,
      );
      this.isRunning = true;
    }
  }

  _exec() {
    if (this._lock) {
      return;
    }

    log('%s #%s Running callback.', this.logPrefix, this.counter);

    this._lock = true;

    Promise.resolve(this.callback(this))
      .then((result) => {
        if (result === false || this._stop) {
          this.stop();
          return;
        }
        this.counter += 1;
        return result;
      })
      .catch((err) => {
        log('%s #%s Callback error: %o', this.logPrefix, this.counter, err);
        console.error(err);
        this.stop();
        if (this.options.errback) {
          this.options.errback(err);
        }
      })
      .finally(() => {
        this._lock = false;
      });
  }

  manualPoll() {
    return this.callback(this);
  }

  stop() {
    log('%s Stopping...', this.logPrefix);
    this._stop = true;
    this.isRunning = false;

    if (this._tid != null) {
      log('%s Clearing Interval ID %s.', this.logPrefix, this._tid);
      window.clearInterval(this._tid);
      this._lock = null;
      this._cb = null;
      this._tid = null;
      return;
    }

    log('%s Timeout ID was empty.', this.logPrefix);
  }
}
